import React from 'react';
import { XCircle, CheckCircle2, BookOpen, Shield, Leaf, Activity, Star, Quote, ArrowRight } from 'lucide-react';
// ─── 1. CONSEQUENCES SECTION ───────────────────────────────────────────────
interface ConsequencesSectionProps {
  showCTA?: boolean;
}
export const ConsequencesSection: React.FC<ConsequencesSectionProps> = ({
  showCTA
}) => {
  const consequences = ['Más medicamentos', 'Más visitas al veterinario', 'Tratamientos dermatológicos recurrentes', 'Dietas "especializadas" costosas', 'Problemas que vuelven una y otra vez'];
  return <section className="py-16 md:py-20 px-4 relative overflow-hidden" style={{
    backgroundColor: '#F8F6F1'
  }} data-id="element-678">
      <div className="absolute inset-0 opacity-5" data-id="element-679">
        <div className="absolute top-1/2 left-1/2 w-[600px] h-[600px] rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" style={{
        backgroundColor: '#C0392B'
      }} data-id="element-680" />
      </div>

      <div className="relative max-w-3xl mx-auto" data-id="element-681">
        <div className="inline-flex items-center gap-2 border text-xs font-bold uppercase tracking-widest mb-8 px-4 py-1.5 rounded-full" style={{
        backgroundColor: 'rgba(192,57,43,0.08)',
        borderColor: 'rgba(192,57,43,0.3)',
        color: '#C0392B'
      }} data-id="element-682">
          ⚠️ Si no haces nada
        </div>

        <h2 className="text-2xl md:text-3xl font-black mb-3 leading-tight" style={{
        color: '#2F5D50'
      }} data-id="element-683">
          Lo más probable es que ocurra{' '}
          <span style={{
          color: '#C0392B'
        }} data-id="element-684">
            esto:
          </span>
        </h2>

        <div className="mt-8 space-y-3 mb-10" data-id="element-685">
          {consequences.map((item, i) => <div key={i} className="flex items-center gap-3 rounded-lg px-5 py-4 bg-white border" style={{
          borderColor: 'rgba(192,57,43,0.2)'
        }} data-id="element-686">
              <XCircle className="w-5 h-5 flex-shrink-0" style={{
            color: '#C0392B'
          }} data-id="element-687" />
              <span className="font-medium text-base text-gray-700" data-id="element-688">
                {item}
              </span>
            </div>)}
        </div>

        <div className="w-16 h-1 mb-10 rounded-full" style={{
        backgroundColor: '#C0392B'
      }} data-id="element-689" />

        <div className="space-y-4" data-id="element-690">
          <p className="text-xl md:text-2xl font-bold text-gray-700 leading-relaxed" data-id="element-691">
            Y cada año que pasa…{' '}
            <span style={{
            color: '#C0392B'
          }} data-id="element-692">
              El cuerpo se debilita más.
            </span>
          </p>

          <div className="bg-white pl-5 py-4 rounded-r-xl border-l-4" style={{
          borderColor: '#C0392B'
        }} data-id="element-693">
            <p className="text-gray-600 text-base md:text-lg leading-relaxed" data-id="element-694">
              No porque tu perro esté{' '}
              <span className="italic text-gray-400" data-id="element-695">"envejeciendo"</span>.
            </p>
            <p className="font-bold text-base md:text-lg mt-2 text-gray-800" data-id="element-696">
              Sino porque su sistema digestivo{' '}
              <span style={{
              color: '#C0392B'
            }} data-id="element-697">
                nunca se recuperó.
              </span>
            </p>
          </div>

          {showCTA && <div className="pt-6 text-center" data-id="element-698">
              <button onClick={() => {
            document.getElementById('benefits-section')?.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }} className="group relative text-white text-base md:text-lg font-bold py-5 px-10 rounded-2xl shadow-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl inline-flex items-center gap-3 overflow-hidden text-center leading-tight" style={{
            background: 'linear-gradient(to right, #F57C00, #e07000)',
            boxShadow: '0 10px 30px rgba(245,124,0,0.35)'
          }} onMouseEnter={e => e.currentTarget.style.background = 'linear-gradient(to right, #E66A00, #cc5f00)'} onMouseLeave={e => e.currentTarget.style.background = 'linear-gradient(to right, #F57C00, #e07000)'} data-id="element-699">
                <span className="relative z-10 flex items-center gap-3" data-id="element-700">
                  QUIERO SALVAR LA VIDA DE MI MASCOTA
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform hidden md:block" strokeWidth={3} data-id="element-701" />
                </span>
                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 skew-y-12" data-id="element-702" />
              </button>
            </div>}
        </div>
      </div>
    </section>;
};
// ─── 2. PROTOCOL INTRO SECTION ─────────────────────────────────────────────
export const ProtocolIntroSection: React.FC = () => {
  const benefits = ['Reducir inflamación intestinal', 'Restaurar la microbiota', 'Mejorar absorción de nutrientes', 'Disminuir reacciones alérgicas', 'Mejorar energía y vitalidad'];
  return <section id="protocol-intro" className="py-16 md:py-24 px-4 relative overflow-hidden" style={{
    backgroundColor: '#F8F6F1'
  }} data-id="element-703">
      <div className="absolute inset-0 opacity-5" data-id="element-704">
        <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full blur-3xl" style={{
        backgroundColor: '#6DAA7F'
      }} data-id="element-705" />
      </div>

      <div className="relative max-w-3xl mx-auto" data-id="element-706">
        <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-10 pl-5" style={{
        borderLeft: '4px solid #F57C00'
      }} data-id="element-707">
          Después de analizar este patrón repetido durante años, se desarrolló
          un protocolo simple:
        </p>

        <p className="text-gray-700 text-lg md:text-xl font-semibold mb-8" data-id="element-708">
          Un sistema estructurado de nutrición natural diseñado para:
        </p>

        <div className="space-y-3 mb-12" data-id="element-709">
          {benefits.map((item, i) => <div key={i} className="flex items-center gap-3 rounded-lg px-5 py-4 bg-white border" style={{
          borderColor: '#6DAA7F'
        }} data-id="element-710">
              <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{
            color: '#6DAA7F'
          }} data-id="element-711" />
              <span className="font-semibold text-base" style={{
            color: '#2F5D50'
          }} data-id="element-712">
                {item}
              </span>
            </div>)}
        </div>

        <div className="flex items-center gap-4 mb-10" data-id="element-713">
          <div className="flex-1 h-px" style={{
          backgroundColor: '#6DAA7F',
          opacity: 0.3
        }} data-id="element-714" />
          <span className="text-gray-400 text-sm uppercase tracking-widest" data-id="element-715">
            Así nació el
          </span>
          <div className="flex-1 h-px" style={{
          backgroundColor: '#6DAA7F',
          opacity: 0.3
        }} data-id="element-716" />
        </div>

        <div className="rounded-2xl p-8 text-center border-2" style={{
        background: 'linear-gradient(135deg, rgba(47,93,80,0.08), #F8F6F1)',
        borderColor: '#6DAA7F'
      }} data-id="element-717">
          <p className="text-xs font-bold uppercase tracking-[0.3em] mb-3" style={{
          color: '#6DAA7F'
        }} data-id="element-718">
            Presentamos
          </p>
          <h2 className="text-2xl md:text-4xl font-black uppercase leading-tight" style={{
          color: '#2F5D50'
        }} data-id="element-719">
            Programa Clínico de{' '}
            <span style={{
            color: '#6DAA7F'
          }} data-id="element-720">
              Nutrición Natural
            </span>
            <br data-id="element-721" />
            para Perros
          </h2>
        </div>
      </div>
    </section>;
};
// ─── 3. PROGRAM INCLUDES SECTION ───────────────────────────────────────────
interface ProgramIncludesSectionProps {
  showCTA?: boolean;
}
export const ProgramIncludesSection: React.FC<ProgramIncludesSectionProps> = ({
  showCTA
}) => {
  const includes = [{
    icon: '📋',
    text: '30 recetas naturales balanceadas'
  }, {
    icon: '⚖️',
    text: 'Guía de cantidades según peso'
  }, {
    icon: '🔄',
    text: 'Sistema de rotación proteica'
  }, {
    icon: '📅',
    text: 'Plan de transición de 7 días'
  }, {
    icon: '✅',
    text: 'Lista de alimentos seguros y prohibidos'
  }, {
    icon: '🛡️',
    text: 'Rutina diaria de prevención'
  }];
  return <section className="py-16 md:py-20 px-4 relative overflow-hidden" style={{
    backgroundColor: '#F8F6F1'
  }} data-id="element-722">
      <div className="absolute top-0 left-0 w-64 h-64 rounded-full blur-3xl opacity-10" style={{
      backgroundColor: '#6DAA7F'
    }} data-id="element-723" />

      <div className="relative max-w-3xl mx-auto" data-id="element-724">
        <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{
        color: '#6DAA7F'
      }} data-id="element-725">
          Contenido del programa
        </p>
        <h2 className="text-2xl md:text-3xl font-black mb-10 leading-tight" style={{
        color: '#2F5D50'
      }} data-id="element-726">
          Este programa incluye:
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-12" data-id="element-727">
          {includes.map((item, i) => <div key={i} className="flex items-center gap-3 bg-white border rounded-xl px-4 py-4" style={{
          borderColor: '#6DAA7F'
        }} data-id="element-728">
              <span className="text-xl flex-shrink-0" data-id="element-729">{item.icon}</span>
              <span className="font-medium text-sm leading-snug text-gray-700" data-id="element-730">
                {item.text}
              </span>
            </div>)}
        </div>

        <div className="w-16 h-1 mb-10 rounded-full" style={{
        backgroundColor: '#6DAA7F'
      }} data-id="element-731" />

        <div className="space-y-4" data-id="element-732">
          <div className="flex items-start gap-3" data-id="element-733">
            <span className="font-black text-xl mt-0.5" style={{
            color: '#6DAA7F'
          }} data-id="element-734">
              →
            </span>
            <div data-id="element-735">
              <p className="text-gray-400 line-through text-sm mb-0.5" data-id="element-736">
                No es improvisación.
              </p>
              <p className="font-bold text-lg" style={{
              color: '#2F5D50'
            }} data-id="element-737">
                Es estructura.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3" data-id="element-738">
            <span className="font-black text-xl mt-0.5" style={{
            color: '#6DAA7F'
          }} data-id="element-739">
              →
            </span>
            <div data-id="element-740">
              <p className="text-gray-400 line-through text-sm mb-0.5" data-id="element-741">
                No es solo "dar comida casera".
              </p>
              <p className="font-bold text-lg text-gray-800" data-id="element-742">
                Es hacerlo de forma{' '}
                <span style={{
                color: '#2F5D50'
              }} data-id="element-743">
                  segura y organizada.
                </span>
              </p>
            </div>
          </div>

          {showCTA && <div className="pt-6 text-center" data-id="element-744">
              <button onClick={() => {
            document.getElementById('benefits-section')?.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }} className="group relative text-white text-base md:text-lg font-bold py-5 px-10 rounded-2xl shadow-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl inline-flex items-center gap-3 overflow-hidden text-center leading-tight" style={{
            background: 'linear-gradient(to right, #F57C00, #e07000)',
            boxShadow: '0 10px 30px rgba(245,124,0,0.35)'
          }} onMouseEnter={e => e.currentTarget.style.background = 'linear-gradient(to right, #E66A00, #cc5f00)'} onMouseLeave={e => e.currentTarget.style.background = 'linear-gradient(to right, #F57C00, #e07000)'} data-id="element-745">
                <span className="relative z-10 flex items-center gap-3" data-id="element-746">
                  QUIERO SALVAR LA VIDA DE MI MASCOTA
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform hidden md:block" strokeWidth={3} data-id="element-747" />
                </span>
                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 skew-y-12" data-id="element-748" />
              </button>
            </div>}
        </div>
      </div>
    </section>;
};
// ─── 4. AUTHOR SECTION ─────────────────────────────────────────────────────
export const AuthorSection: React.FC = () => {
  return <section className="py-16 md:py-20 px-4 relative overflow-hidden" style={{
    backgroundColor: '#F8F6F1'
  }} data-id="element-749">
      <div className="absolute inset-0 opacity-5" data-id="element-750">
        <div className="absolute top-0 right-1/3 w-80 h-80 rounded-full blur-3xl" style={{
        backgroundColor: '#6DAA7F'
      }} data-id="element-751" />
      </div>

      <div className="relative max-w-3xl mx-auto" data-id="element-752">
        <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{
        color: '#6DAA7F'
      }} data-id="element-753">
          Respaldo científico
        </p>

        <div className="bg-white border rounded-2xl p-8 md:p-10 shadow-sm" style={{
        borderColor: '#6DAA7F'
      }} data-id="element-754">
          <div className="flex items-center gap-4 mb-8" data-id="element-755">
            <div className="w-20 h-20 rounded-full overflow-hidden flex-shrink-0 border-2" style={{
            borderColor: '#6DAA7F'
          }} data-id="element-756">
              <img src="https://cdn.magicpatterns.com/uploads/t5cXdZagcLmQy5ATjKH1gn/image.png" alt="Dr. Rennan Viegas" className="w-full h-full object-cover object-top" data-id="element-757" />
            </div>
            <div data-id="element-758">
              <h3 className="text-xl font-black" style={{
              color: '#2F5D50'
            }} data-id="element-759">
                Dr. Rennan Viegas
              </h3>
              <p className="text-sm font-semibold" style={{
              color: '#6DAA7F'
            }} data-id="element-760">
                Médico Veterinario · +18 años de experiencia
              </p>
            </div>
          </div>

          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-6" data-id="element-761">
            El protocolo está inspirado en la metodología del{' '}
            <span className="font-bold" style={{
            color: '#2F5D50'
          }} data-id="element-762">
              Dr. Rennan Viegas
            </span>
            , médico veterinario con más de 18 años de experiencia en nutrición
            preventiva.
          </p>

          <p className="text-gray-500 text-base leading-relaxed mb-6" data-id="element-763">
            Su enfoque ha ayudado a miles de dueños a mejorar la salud digestiva
            de sus mascotas.
          </p>

          <div className="rounded-xl p-5" style={{
          backgroundColor: '#FFF3E0',
          border: '1px solid #6DAA7F'
        }} data-id="element-764">
            <p className="text-gray-700 text-base leading-relaxed" data-id="element-765">
              Este programa adapta esos principios a un sistema{' '}
              <span className="font-bold" style={{
              color: '#2F5D50'
            }} data-id="element-766">
                práctico y accesible
              </span>{' '}
              para cualquier dueño.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4 mt-8" data-id="element-767">
            <div className="text-center" data-id="element-768">
              <p className="text-2xl font-black" style={{
              color: '#2F5D50'
            }} data-id="element-769">
                +18
              </p>
              <p className="text-xs text-gray-400 mt-1" data-id="element-770">años de experiencia</p>
            </div>
            <div className="text-center border-x" style={{
            borderColor: '#6DAA7F'
          }} data-id="element-771">
              <p className="text-2xl font-black" style={{
              color: '#2F5D50'
            }} data-id="element-772">
                +620K
              </p>
              <p className="text-xs text-gray-400 mt-1" data-id="element-773">seguidores</p>
            </div>
            <div className="text-center" data-id="element-774">
              <p className="text-2xl font-black" style={{
              color: '#2F5D50'
            }} data-id="element-775">
                Miles
              </p>
              <p className="text-xs text-gray-400 mt-1" data-id="element-776">de mascotas ayudadas</p>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
// ─── 5. WRITTEN TESTIMONIALS SECTION ───────────────────────────────────────
export const WrittenTestimonialsSection: React.FC = () => {
  const testimonials = [{
    name: 'Carolina Méndez',
    city: 'Bogotá, Colombia',
    result: 'Mi perro dejó de rascarse en 3 semanas',
    text: 'Llevaba más de un año yendo al veterinario por la dermatitis de Toby. Le daban cremas, antihistamínicos, dietas especiales… y siempre volvía. Desde que empecé con el programa, en menos de 3 semanas dejó de rascarse. No lo podía creer. Su pelo volvió a crecer y está lleno de energía.',
    dog: 'Toby, Golden Retriever, 4 años',
    stars: 5,
    avatar: 'https://i.pravatar.cc/150?img=47'
  }, {
    name: 'Andrés Villamizar',
    city: 'Medellín, Colombia',
    result: 'Adiós a los problemas digestivos crónicos',
    text: "Rex tenía heces blandas casi todos los días. El veterinario decía que era 'sensibilidad digestiva' y nos recomendaba croquetas premium carísimas. Con el programa entendí que el problema era la inflamación intestinal. Hoy lleva 2 meses sin un solo episodio digestivo. Increíble.",
    dog: 'Rex, Pastor Alemán, 6 años',
    stars: 5,
    avatar: 'https://i.pravatar.cc/150?img=12'
  }, {
    name: 'Valentina Torres',
    city: 'Cali, Colombia',
    result: 'Más energía y menos alergias en 1 mes',
    text: 'Luna estaba siempre cansada, con los ojos llorosos y se lamía las patas constantemente. Pensé que era algo genético. Seguí el protocolo al pie de la letra y al mes ya era otra perra. Juega, corre, duerme bien. La diferencia es brutal. Ojalá lo hubiera encontrado antes.',
    dog: 'Luna, Labrador, 3 años',
    stars: 5,
    avatar: 'https://i.pravatar.cc/150?img=32'
  }];
  return <section id="written-testimonials" className="py-16 md:py-24 px-4 relative overflow-hidden" style={{
    backgroundColor: '#F8F6F1'
  }} data-id="element-777">
      <div className="absolute inset-0 opacity-5" data-id="element-778">
        <div className="absolute bottom-0 left-1/4 w-96 h-96 rounded-full blur-3xl" style={{
        backgroundColor: '#6DAA7F'
      }} data-id="element-779" />
      </div>

      <div className="relative max-w-3xl mx-auto" data-id="element-780">
        <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{
        color: '#6DAA7F'
      }} data-id="element-781">
          Resultados reales
        </p>
        <h2 className="text-2xl md:text-3xl font-black mb-3 leading-tight" style={{
        color: '#2F5D50'
      }} data-id="element-782">
          Lo que dicen quienes ya lo aplicaron
        </h2>
        <p className="text-gray-500 text-base mb-10" data-id="element-783">
          Testimonios verificados de dueños reales.
        </p>

        <div className="space-y-6" data-id="element-784">
          {testimonials.map((t, i) => <div key={i} className="bg-white border rounded-2xl p-6 md:p-8 relative overflow-hidden shadow-sm" style={{
          borderColor: '#6DAA7F'
        }} data-id="element-785">
              <Quote className="absolute top-4 right-4 w-8 h-8 opacity-10" style={{
            color: '#2F5D50'
          }} data-id="element-786" />

              <div className="flex gap-1 mb-4" data-id="element-787">
                {Array.from({
              length: t.stars
            }).map((_, s) => <Star key={s} className="w-4 h-4 fill-current" style={{
              color: '#F57C00'
            }} data-id="element-788" />)}
              </div>

              <p className="font-bold text-base mb-3" style={{
            color: '#2F5D50'
          }} data-id="element-789">
                "{t.result}"
              </p>

              <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-5" data-id="element-790">
                {t.text}
              </p>

              <div className="flex items-center gap-3 pt-4 border-t" style={{
            borderColor: '#e8f5ec'
          }} data-id="element-791">
                <div className="w-12 h-12 rounded-full overflow-hidden border-2 flex-shrink-0" style={{
              borderColor: '#6DAA7F'
            }} data-id="element-792">
                  <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" data-id="element-793" />
                </div>
                <div data-id="element-794">
                  <p className="font-bold text-sm" style={{
                color: '#2F5D50'
              }} data-id="element-795">
                    {t.name}
                  </p>
                  <p className="text-gray-400 text-xs" data-id="element-796">{t.city}</p>
                  <p className="text-xs mt-0.5" style={{
                color: '#6DAA7F'
              }} data-id="element-797">
                    {t.dog}
                  </p>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
};